/**
 * Every line the port notice says to a player (D163, platform.md §6) — in
 * one place, named by the fact it answers, so the words are the design
 * lane's to re-cut without touching the wiring. The register is the account
 * control's (D157 addendum): plain, second person, the chrome's voice and
 * never EURYDICE's — a version of a game is a fact about the platform, not
 * about the facility.
 *
 * What the notice can say is exactly what the report carries: WHICH command
 * lands differently, its place among the player's commands, and in what
 * respect. Never the prose, never a guess, and never a room or an item by
 * name — in server mode the shell holds no bundle, so it has no names to
 * give.
 *
 * **The notice only ever appears when the change COSTS the player
 * something.** A version whose every command still lands is ported in
 * silence; the scrollback simply re-renders in the new version's words.
 * So the heading names the consequence rather than the release: "a newer
 * version is out" is true of the silent ports too, and would be the one
 * fact this notice is not about.
 *
 * @packageDocumentation
 */

import type { PortDifference, PortReport, TurnVerdict } from '@orpheus/api-contracts';

/**
 * What each verdict looks like from the player's chair, as a BARE
 * INFINITIVE so one table serves both halves of the contrast — "it would
 * BE REFUSED where it used to GO THROUGH".
 *
 * The report carries `was` and `now`, and they are the most concrete facts
 * in it: a player who hears "it would not go the same way" has learned
 * nothing they could not guess from the notice existing. Two verdict names
 * turn that into the one sentence they actually want.
 */
const VERDICT_READINGS: Readonly<Record<TurnVerdict, string>> = {
	action_rejected: 'be refused',
	clarification_required: 'be met with a question',
	executed: 'go through',
	/*
	 * D27: an exact-input reaction. "Answered without changing anything" was
	 * the MECHANISM — the game speaks, the world does not move — described to
	 * a player who never knew the mechanism existed, and read in place
	 * (setup.md §12.3, the gatehouse's `xyzzy`) it made the longest and least
	 * natural clause in the paragraph. What the player actually saw is that
	 * the game had a reply ready for those exact words, so that is what it
	 * says. Whether the world moved is not a fact they can check, and not one
	 * that helps them decide.
	 */
	/*
	 * D165: the platform's one verdict. A turn refused at the edge of the demo
	 * is never appended to the session, so a port never compares one — the
	 * entry exists because the verdict does, and reads honestly if it ever
	 * is met. Calder's to re-cut with the rest.
	 */
	not_entitled: 'be stopped at the edge of the demo',
	parse_failed: 'not be understood',
	reaction: 'get its own answer',
	session_ended: 'come after the story had ended',
};

/**
 * What each difference means to the player, as the clause that follows the
 * colon. `verdict` is absent on purpose: it is built from `was` and `now`
 * rather than being one fixed sentence.
 */
const DIFFERENCE_CLAUSES: Readonly<Record<Exclude<PortDifference, 'verdict'>, string>> = {
	carried: 'you would be carrying something different',
	ending: 'the story would have ended differently',
	question: 'the game would be asking you something else',
	room: 'it would take you somewhere else',
};

/**
 * The two answers, as the buttons read — each one names what it DOES to the
 * player's game, because a bare "Update" read as the fix (D248).
 *
 * @remarks
 * Mike pressed "Update" on 2026-10-31 and lost an act's worth of play to
 * it: the notice's heading said his game "cannot follow" the new version,
 * which made the button read as the way to follow it, and its cost sat in
 * the middle of the paragraph as a count. A button whose consequence is
 * losing turns says so in its own words; keeping the game is the first
 * answer, because it is the one that cannot cost anything.
 *
 * @param report - The replay's report
 * @returns Both labels
 */
export const portChoiceLabelsFor = (report: PortReport): { readonly stay: string; readonly update: string } =>
	report.cause === 'engine'
		? { stay: 'Carry on as it plays now', update: report.keptCommands === 0 ? 'Start over' : 'Rewind to before it' }
		: { stay: 'Keep my game', update: report.keptCommands === 0 ? 'Update and start over' : 'Update and rewind' };

/** The one-line statement above the detail: the cost of the move, not the news of a release. */
export const PORT_HEADING = 'This game has been updated, and moving your game to the new version would rewind it.';

/**
 * D257: the heading when the ENGINE changed under the same version — the
 * game as it was played is gone, so the news is that the past plays
 * differently, and the choice is how to meet that, not whether to move.
 */
export const ENGINE_CHANGE_HEADING = 'The game has been updated, and some of your earlier commands now play differently.';

/**
 * The heading for this report.
 *
 * @param report - The replay's report
 * @returns The version heading, or the engine one (D257)
 */
export const portHeadingFor = (report: PortReport): string => (report.cause === 'engine' ? ENGINE_CHANGE_HEADING : PORT_HEADING);

/** The small ordinals as words; above ten, digits read better than "forty-first". */
const ORDINAL_WORDS = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'] as const;

/**
 * One ordinal, in the form a sentence wants.
 *
 * @param position - The one-based place
 * @returns "first" … "tenth", then "11th", "21st", "42nd"
 */
const ordinal = (position: number): string => {
	const word = ORDINAL_WORDS[position - 1];

	if (word !== undefined) {
		return word;
	}

	const lastTwo = position % 100;
	const last = position % 10;
	const suffix = lastTwo >= 11 && lastTwo <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[last] ?? 'th');

	return `${String(position)}${suffix}`;
};

/**
 * "a, and b" / "a, b, and c" — a list in a sentence.
 *
 * @remarks
 * The comma before "and" is kept even for a pair, against the usual rule,
 * because these items are CLAUSES rather than nouns: without it, "where it
 * used to go through and you would be carrying something different" reads
 * as one sentence that lost its way.
 *
 * @param clauses - The readings, in the report's order
 * @returns The joined list
 */
const listed = (clauses: readonly string[]): string =>
	clauses.length <= 1 ? (clauses[0] ?? '') : `${clauses.slice(0, -1).join(', ')}, and ${clauses.at(-1) ?? ''}`;

/**
 * Every difference, in the player's terms, in the report's order.
 *
 * @param divergedAt - The command that lands differently
 * @returns The clauses, `verdict` built from the two verdict names
 */
const differenceClauses = (divergedAt: PortReport['divergedAt']): readonly string[] =>
	divergedAt.differences.map((difference) =>
		difference === 'verdict'
			? `it would ${VERDICT_READINGS[divergedAt.now]} where it used to ${VERDICT_READINGS[divergedAt.was]}`
			: DIFFERENCE_CLAUSES[difference],
	);

/**
 * What updating costs, said FIRST and as a place in the player's own game:
 * back to just before the command they typed, and how many of their last
 * commands go with it.
 *
 * @remarks
 * The place is stable and the count is not: Update recomputes from wherever
 * the session stands when the button is pressed, so it always rewinds to
 * just before the same command, while a player who reads this, plays three
 * more turns, and then updates loses those three as well. Hence "anything
 * you play before you choose" — the numbers are from the moment the notice
 * opened and do not tick.
 *
 * A sentence rather than a live count, deliberately (Lyra's lean, and
 * mine): numbers that climbed while the player played would turn a
 * decision into a pressure, and this notice is `role="status"` — a live
 * region that changes every turn is noise to anyone listening to it rather
 * than looking at it.
 *
 * D257: on an engine change the same sentence names the rewind as what it
 * is — there is no new version to move to — and the place it parts.
 *
 * @param report - The replay's report
 * @returns The sentence
 */
const costOf = ({ cause, divergedAt, keptCommands, totalCommands }: PortReport): string => {
	const choosing = cause === 'engine' ? 'Rewinding' : 'Updating';

	if (keptCommands === 0) {
		const parting = cause === 'engine' ? 'the game now plays differently' : 'the new version parts from your game';

		return `${choosing} would start you over from the beginning — your first command, “${divergedAt.command}”, is where ${parting}.`;
	}

	const lost = totalCommands - keptCommands;
	const lastCommands = lost === 1 ? 'your last command' : `your last ${String(lost)} commands`;

	return `${choosing} would take you back to just before your ${ordinal(divergedAt.position)} command, “${divergedAt.command}”, and you would lose ${lastCommands} — and anything you play before you choose.`;
};

/**
 * What each answer costs, then why.
 *
 * @remarks
 * The cost leads (D248): it is the one fact the choice turns on, and a
 * player who reads a single sentence must read that one. The command's own
 * words carry it, because that is the part a player recognizes — they
 * typed it. Why that command parts the versions follows, then what keeping
 * the game means.
 *
 * The last sentence says what KEEPING means and not merely that it is
 * harmless: this version is never offered again, so a player who keeps
 * their game and then never sees the notice would otherwise be entitled to
 * think something had gone wrong.
 *
 * @param report - The replay's report
 * @returns The detail paragraph
 */
export const describePortReport = (report: PortReport): string => {
	const clause = listed(differenceClauses(report.divergedAt));

	// D257: no version to move to — rewinding is the cost, and carrying on keeps everything as it now plays.
	if (report.cause === 'engine') {
		return [
			costOf(report),
			`Since the update, that command lands differently: ${clause}.`,
			'Carrying on keeps every command, played the way the game plays them now.',
		].join(' ');
	}

	return [
		costOf(report),
		`On the new version, that command lands differently: ${clause}.`,
		'Keeping your game leaves it exactly as it is, and this version will not be offered again.',
	].join(' ');
};
